import { motion } from 'framer-motion';
import type { CSSProperties } from 'react';
import type { VoteChoice } from 'shared';
import { ro } from '../i18n/ro';
import { cx } from './tokens';

export type CardFace = 'DOWN' | 'PENDING' | VoteChoice;

const SIZES = {
  sm: { box: 'h-12 w-9 text-[0.6rem] border-2', press: '2px' },
  md: { box: 'h-20 w-14 text-sm border-[3px]', press: '3px' },
  lg: { box: 'h-[18cqh] w-[12cqh] min-h-12 min-w-8 text-[2.7cqh] border-4', press: '5px' },
} as const;

const FACE =
  'inner-rule inner-rule-tight relative flex h-full w-full items-center justify-center overflow-hidden border-ink';

/**
 * A ballot card. Face-down while the vote is hidden, then turned in place to
 * reveal Ja (teal) or Nein (red) — the same two colours as the printed cards.
 *
 * The turn is a real rotation with two backface-hidden faces, so the card
 * behaves like card stock rather than a cross-fade. `delay` staggers a row by a
 * few tens of milliseconds so the reveal reads as one wave rather than a
 * mechanical snap, while still being simultaneous to the eye.
 */
export function VoteCard({
  face,
  size = 'md',
  delay = 0,
  animate = true,
  className,
}: {
  face: CardFace;
  size?: keyof typeof SIZES;
  delay?: number;
  /** False for a device that arrived after the reveal: render the end state. */
  animate?: boolean;
  className?: string;
}) {
  const s = SIZES[size];
  const vars = { '--press': s.press } as CSSProperties;

  /*
   * A card not yet cast. Outlined and dashed rather than filled, so the
   * projector shows at a glance who the table is still waiting on — it has to
   * survive being read across a lit room, so it cannot be a low-contrast tint.
   */
  if (face === 'PENDING') {
    return (
      <div className={cx('relative select-none', s.box, className)} style={vars}>
        <div className="flex h-full w-full items-center justify-center border-[3px] border-dashed border-ink/50 bg-paper">
          <span className="deco-caps text-ink-faint opacity-70" aria-label={ro.notVotedYet}>
            ?
          </span>
        </div>
      </div>
    );
  }

  const choice = face === 'DOWN' ? null : face;
  const isJa = choice === 'JA';

  /*
   * The press shadow belongs on the scene, not on the faces: a face carries
   * `position: absolute` from `.flip-face`, which `.pressed`'s `position:
   * relative` would override — and a shadow attached to a rotating face would
   * turn with it anyway. On the static scene it reads as the card lying on the
   * table.
   */
  return (
    <div className={cx('flip-scene pressed select-none', s.box, className)} style={vars}>
      <motion.div
        className="flip-inner"
        initial={animate ? false : { rotateY: choice ? 180 : 0 }}
        animate={{ rotateY: choice ? 180 : 0 }}
        transition={
          animate
            ? { duration: 0.55, delay, ease: [0.2, 0.9, 0.25, 1] }
            : { duration: 0 }
        }
      >
        {/* Card back: halftone field with a deco lozenge. */}
        <div className="flip-face">
          <div className={cx(FACE, 'bg-ink')} aria-label="—">
            <div className="halftone absolute inset-0 text-paper" />
            <div className="relative h-1/2 w-1/2 rotate-45 border-2 border-paper/70" />
          </div>
        </div>

        <div className="flip-face flip-face-back">
          <div
            className={cx(FACE, isJa ? 'bg-teal' : 'bg-red')}
            aria-label={isJa ? ro.ja : ro.nein}
          >
            <div className="halftone absolute inset-0 text-paper" />
            <span className="relative font-display uppercase tracking-widest text-paper-raised">
              {isJa ? ro.ja : ro.nein}
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
